const QUAL_CODE_MAP: Record<string, string> = {
  undergraduate: "m",
  postgraduate: "l",
  "foundation-degree": "a",
};

const QUAL_CODE_REVERSE: Record<string, string> = {
  m: "undergraduate",
  l: "postgraduate",
  a: "foundation-degree",
};

export interface OpenDaysFilterState {
  studyLevel: string;
  provider: string;       // institution text key (for display/filtering)
  providerId: string;     // institution ID (for URL)
  location: string;
  distance: string;
  postcode: string;
  monthYear: string;
  eventType: string;
  pageno: string;
  dateFrom?: string;
  dateTo?: string;
  isVirtualTour?: boolean; // for date=virtual_tour parameter
}

/**
 * Parse URL search params into filter state.
 */
export function parseOpenDaysSearchParams(searchParams: Record<string, string | undefined>): OpenDaysFilterState {
  const decodeParam = (param: string | undefined) => {
    if (!param) return "";
    try {
      // First replace %2C with comma, then decode, then handle &amp;
      const withCommas = param.replace(/%2C/g, ",");
      const decoded = decodeURIComponent(withCommas);
      return decoded.replace(/&amp;/g, "&");
    } catch {
      return param.replace(/%2C/g, ",").replace(/&amp;/g, "&");
    }
  };

  const level = decodeParam(searchParams?.level);
  const datesParam = decodeParam(searchParams?.dates);
  const dateParam = decodeParam(searchParams?.date);

  let dateFrom = "";
  let dateTo = "";
  let monthYear = "";
  let isVirtualTour = false;

  if (datesParam && datesParam.includes(",")) {
    const [fromStr, toStr] = datesParam.split(",");
    dateFrom = parseDDMMYYYYToISO(fromStr);
    dateTo = parseDDMMYYYYToISO(toStr);
  } else if (dateParam) {
    if (dateParam === "virtual_tour") {
      isVirtualTour = true;
    } else if (dateParam.includes(",")) {
      const [fromStr, toStr] = dateParam.split(",");
      dateFrom = parseDateParam(fromStr);
      dateTo = parseDateParam(toStr);
    } else {
      monthYear = parseDateParamToMonthYear(dateParam);
    }
  }

  return {
    studyLevel: QUAL_CODE_REVERSE[level] || "",
    provider: decodeParam(searchParams?.provider),
    providerId: decodeParam(searchParams?.collegeid),
    location: decodeParam(searchParams?.location),
    distance: decodeParam(searchParams?.distance) || "any",
    postcode: decodeParam(searchParams?.postcode),
    monthYear,
    eventType: decodeParam(searchParams?.["event-type"]),
    pageno: decodeParam(searchParams?.pageno) || "1",
    dateFrom,
    dateTo,
    isVirtualTour,
  };
}

/**
 * Build URL from filter state.
 * Returns: /open-days/search/?level=M&collegeid=3756&location=north-east-england&date=042026
 */
export function buildOpenDaysUrl(filters: Partial<OpenDaysFilterState>): string {
  const parts: string[] = [];

  if (filters.studyLevel) {
    const code = QUAL_CODE_MAP[filters.studyLevel];
    if (code) parts.push(`level=${code}`);
  }

  if (filters.providerId) {
    parts.push(`collegeid=${filters.providerId}`);
  }

  if (filters.location) {
    parts.push(`location=${filters.location}`);
  }

  // Always include distance when postcode is provided (default "any")
  if (filters.postcode) {
    parts.push(`distance=${filters.distance || "any"}`);
    parts.push(`postcode=${filters.postcode.replace(/\s/g, "+")}`);
  } else if (filters.distance && filters.distance !== "any") {
    parts.push(`distance=${filters.distance}`);
  }

  if (filters.isVirtualTour) {
    parts.push(`date=virtual_tour`);
  } else if (filters.dateFrom && filters.dateTo) {
    const from = formatISOToDDMMYYYY(filters.dateFrom);
    const to = formatISOToDDMMYYYY(filters.dateTo);
    parts.push(`dates=${from},${to}`);
  } else if (filters.monthYear) {
    parts.push(`date=${formatMonthYearToParam(filters.monthYear)}`);
  }

  if (filters.eventType) {
    parts.push(`event-type=${filters.eventType}`);
  }

  if (filters.pageno && filters.pageno !== "1") {
    parts.push(`pageno=${filters.pageno}`);
  }

  const queryString = parts.join("&");
  return queryString ? `/open-days/search/?${queryString}` : "/open-days/";
}

/**
 * Convert ISO date "2026-04-15" to param format "042026" (MMYYYY)
 */
function formatISOToDDMMYYYY(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}${month}${year}`;
}

function parseDDMMYYYYToISO(ddmmyyyy: string): string {
  if (!ddmmyyyy || ddmmyyyy.length !== 8) return "";
  const day = ddmmyyyy.substring(0, 2);
  const month = ddmmyyyy.substring(2, 4);
  const year = ddmmyyyy.substring(4, 8);
  return `${year}-${month}-${day}`;
}

/**
 * Convert monthYear slug "april-2026" to param format "042026"
 */
function formatMonthYearToParam(monthYear: string): string {
  if (!monthYear) return "";
  const months: Record<string, string> = {
    january: "01", february: "02", march: "03", april: "04",
    may: "05", june: "06", july: "07", august: "08",
    september: "09", october: "10", november: "11", december: "12",
  };
  const [month, year] = monthYear.split("-");
  return `${months[month] || "01"}${year}`;
}

/**
 * Parse date param "042026" to ISO first-of-month "2026-04-01"
 */
function parseDateParam(param: string): string {
  if (!param || param.length !== 6) return "";
  const month = param.slice(0, 2);
  const year = param.slice(2);
  return `${year}-${month}-01`;
}

/**
 * Parse date param "042026" to monthYear slug "april-2026"
 */
function parseDateParamToMonthYear(param: string): string {
  if (!param || param.length !== 6) return "";
  const monthNum = parseInt(param.slice(0, 2), 10);
  const year = param.slice(2);
  const monthNames = [
    "january", "february", "march", "april", "may", "june",
    "july", "august", "september", "october", "november", "december",
  ];
  return `${monthNames[monthNum - 1]}-${year}`;
}

/**
 * Get display-friendly month-year (e.g., "april-2026" → "April 2026")
 */
export function formatMonthYear(monthYear: string): string {
  if (!monthYear) return "";
  const [month, year] = monthYear.split("-");
  return `${month.charAt(0).toUpperCase() + month.slice(1)} ${year}`;
}

/**
 * Build month-year slug from month name and year (e.g., "April", "2026" → "april-2026")
 */
export function buildMonthYearSlug(month: string, year: string): string {
  return `${month.toLowerCase()}-${year}`;
}

/**
 * Get qualification code for a study level
 */
export function getQualCode(studyLevel: string): string {
  return QUAL_CODE_MAP[studyLevel] || "";
}

/**
 * Get study level from qualification code
 */
export function getStudyLevelFromCode(code: string): string {
  return QUAL_CODE_REVERSE[code] || "";
}

const OPENDAYS_SEO_FILTER_KEYS: Array<{
  key: keyof OpenDaysFilterState;
  seoLabel: string;
}> = [
  { key: "studyLevel", seoLabel: "studyLevel" },
  { key: "location", seoLabel: "location" },
  { key: "eventType", seoLabel: "eventType" },
  { key: "providerId", seoLabel: "university" },
  { key: "monthYear", seoLabel: "date" },
];

export function getOpenDaysSEOFieldId(filters: OpenDaysFilterState): string {
  const PROJECT = process.env.PROJECT;
  const parts: string[] = [];

  for (const { key, seoLabel } of OPENDAYS_SEO_FILTER_KEYS) {
    const value = filters[key];
    if (value && value !== "") {
      parts.push(seoLabel);
    }
  }

  if (!filters.monthYear && filters.dateFrom && filters.dateTo) {
    parts.push("date");
  }

  if (filters.isVirtualTour) {
    parts.push("virtualTour");
  }

  const contentString = parts.length === 0 ? "Default" : parts.join(" + ");
  return `SEO - open days - ${contentString} - ${PROJECT}`;
}

export function getOpenDaysMetaDetailsQuery(seoFieldsName: string): string {
  const PROJECT = process.env.PROJECT;
  return `{
  pageTemplateDynamicPageCollection(
    limit: 1
    where: { pageName: "Open Days - Search Result - SR - ${PROJECT}" }
  ) {
    items {
      gaPageName
      pageName
      pageTitle
      urlSlug
      seoFieldsCollection(where: { seoFieldsName: "${seoFieldsName}" }) {
        items {
          seoFieldsName
          robots
          metaTite
          metaKeywords
          metaDescription
          h1Title
          h2Text
          canonical
        }
      }
    }
  }
}`;
}


export interface OpenDaysSEODisplayNames {
  studyLevel?: string;
  location?: string;
  eventType?: string;
  university?: string;
  monthYear?: string;
  totalCount?: number;
}

export function replaceOpenDaysSEOPlaceholders(
  inputText: string | undefined,
  displayNames: OpenDaysSEODisplayNames,
): string {
  if (!inputText) return "";

  const capitalize = (str: string) =>
    str
      .split("-")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");

  const formatMonthYearDisplay = (my: string) => {
    if (!my) return "";
    const [month, year] = my.split("-");
    return `${month.charAt(0).toUpperCase() + month.slice(1)} ${year}`;
  };

  const formatDateParam = (my: string) => {
    if (!my) return "";
    const months: Record<string, string> = {
      january: "01", february: "02", march: "03", april: "04",
      may: "05", june: "06", july: "07", august: "08",
      september: "09", october: "10", november: "11", december: "12",
    };
    const [month, year] = my.split("-");
    return `${months[month.toLowerCase()] || "01"}${year}`;
  };

  let result = inputText;

  result = result.replace("[STUDY LEVEL]", displayNames.studyLevel ? capitalize(displayNames.studyLevel) : "");
  result = result.replace("[LOCATION]", displayNames.location ? capitalize(displayNames.location) : "");
  result = result.replace("[REGION]", displayNames.location ? capitalize(displayNames.location) : "");
  result = result.replace("[EVENT TYPE]", displayNames.eventType ? capitalize(displayNames.eventType) : "");
  result = result.replace("[UNIVERSITY]", displayNames.university || "");
  result = result.replace("[MONTH]", displayNames.monthYear ? formatMonthYearDisplay(displayNames.monthYear) : "");
  result = result.replace("[DATE]", displayNames.monthYear ? formatDateParam(displayNames.monthYear) : "");
  result = result.replace("[COUNT]", displayNames.totalCount ? String(displayNames.totalCount) : "");

  return result.trim();
}
